// Simple circular detection, shouldn't be a problem in react-hook-form values.
// Hopefull 1_000 is deeper than we'll ever actually go...
const DEPTH_LIMIT = 1_000;

const isPlainObject = (thing: unknown): thing is Record<string, unknown> =>
  Boolean(thing && typeof thing === 'object' && (thing.constructor === Object || thing.constructor === null));

/**
 * Compares two currency value objects to determine if they are considered equal.
 *
 * The comparison takes into account the following properties:
 * - `amount`: The numeric value of the currency.
 * - `text`: An textual representation eg: 50%.
 * - `currency`: The currency code.
 * - `isUnknown`: A flag indicating if the value is unknown.
 *
 * The function returns `true` if the objects are considered equal based on a set of rules:
 * - Both are marked as unknown and have the same or both missing currency.
 * - Both have the same numeric amount and currency.
 * - Both have the same numeric amount, empty or missing text, and same currency.
 * - Both have zero amount, same text, and same currency.
 * - Both have the same non-empty text and same currency.
 * - Reference has zero amount, check has undefined/null amount, same currency, and check has no text or is not unknown.
 * - Check is null, reference has zero amount, empty or missing text, is not unknown, and has a currency.
 *
 * @param reference - The reference currency value object.
 * @param check - The currency value object to compare against the reference.
 * @returns `true` if the values are considered equal, otherwise `false`.
 */
const areCurrencyValuesEqual = (reference: Record<string, unknown>, check: Record<string, unknown>): boolean => {
  const refAmount = reference.amount;
  const checkAmount = check.amount;

  const refText = reference.text;
  const checkText = check.text;

  const refCurrency = reference.currency;
  const checkCurrency = check.currency;

  const refIsUnknown = reference.isUnknown;
  const checkIsUnknown = check.isUnknown;

  if (refIsUnknown === true && checkIsUnknown === true) {
    return refCurrency === checkCurrency || (!refCurrency && !checkCurrency);
  }

  if (typeof refAmount === 'number' && typeof checkAmount === 'number' && refCurrency && checkCurrency) {
    if (refAmount === checkAmount && refCurrency === checkCurrency) {
      return true;
    }
  }

  if (
    typeof refAmount === 'number' &&
    typeof checkAmount === 'number' &&
    refAmount === checkAmount &&
    (refText === '' || refText === null || refText === undefined) &&
    (checkText === '' || checkText === null || checkText === undefined) &&
    refCurrency === checkCurrency
  ) {
    return true;
  }

  if (refAmount === 0 && checkAmount === 0 && refText === checkText && refCurrency === checkCurrency) {
    return true;
  }

  if (refText && checkText && refText === checkText && refCurrency === checkCurrency) {
    return true;
  }

  if (refAmount === 0 && (checkAmount === undefined || checkAmount === null) && refCurrency === checkCurrency) {
    const checkHasTextOrUnknown = checkText || checkIsUnknown === true;
    return !checkHasTextOrUnknown;
  }

  if (check === null && refAmount === 0 && (!refText || refText === '') && refIsUnknown === false && refCurrency) {
    return true;
  }

  return false;
};

/**
 * Recursively compares two values for deep equality, with special handling for arrays, plain objects,
 * and a custom 'currency' type. The comparison respects a maximum recursion depth and can handle
 * null and undefined values. For objects, all keys and their corresponding values are compared.
 * For arrays, all elements are compared in order. For the 'currency' type, a custom comparison
 * function is used.
 *
 * @param referenceValue - The first value to compare.
 * @param checkValue - The second value to compare.
 * @param depth - The current recursion depth, used to prevent infinite loops.
 * @param type - (Optional) A string indicating a special comparison type (e.g., 'currency').
 * @returns `true` if the values are deeply equal according to the rules; otherwise, `false`.
 */
const areValuesEqualInternal = (
  referenceValue: unknown,
  checkValue: unknown,
  depth: number,
  type?: string,
): boolean => {
  if (referenceValue === checkValue) {
    return true;
  }

  if (type === 'currency') {
    if (
      typeof referenceValue === 'object' &&
      typeof checkValue === 'object' &&
      referenceValue !== null &&
      checkValue !== null
    ) {
      return areCurrencyValuesEqual(referenceValue as Record<string, unknown>, checkValue as Record<string, unknown>);
    }
    // Currency fields are initially undefined, but then are updated to { amount: 0, currency: "USD" }. We don't want to trigger a change when the currency goes from undefined to zero, as that incorrectly sets the source to manual
    if (
      checkValue === undefined &&
      referenceValue &&
      typeof referenceValue === 'object' &&
      'amount' in referenceValue &&
      referenceValue.amount === 0
    ) {
      return true;
    }

    return false;
  }

  if (
    // prevent infinite loops
    depth === DEPTH_LIMIT ||
    // if either value is null here, they are not equal. both null checked above
    referenceValue === null ||
    checkValue === null ||
    // if either value is undefined here, they are not equal. both undefined checked above
    referenceValue === undefined ||
    checkValue === undefined
  ) {
    return false;
  }

  const referenceIsArray = Array.isArray(referenceValue);
  const checkIsArray = Array.isArray(checkValue);

  if (referenceIsArray && checkIsArray) {
    if (referenceValue.length !== checkValue.length) {
      return false;
    }

    for (let i = 0; i < referenceValue.length; i += 1) {
      if (!areValuesEqualInternal(referenceValue[i], checkValue[i], depth + 1)) {
        return false;
      }
    }
    return true;
  }
  // if either value is an array, but the other is not, they are not equal
  if (referenceIsArray || checkIsArray) {
    return false;
  }

  if (isPlainObject(referenceValue) && isPlainObject(checkValue)) {
    const referenceKeys = Object.keys(referenceValue);

    for (let i = 0; i < referenceKeys.length; i += 1) {
      const key = referenceKeys[i];
      if (!areValuesEqualInternal(referenceValue[key], checkValue[key], depth + 1)) {
        return false;
      }
    }

    const checkKeys = Object.keys(checkValue);
    for (let i = 0; i < checkKeys.length; i += 1) {
      const key = checkKeys[i];
      if (checkValue[key] !== undefined && !(key in referenceValue)) {
        return false;
      }
    }

    return true;
  }

  return false;
};

/**
 * Compares two values for equality, optionally considering a specific type.
 *
 * @param {unknown} referenceValue - The reference value to compare against.
 * @param {unknown} checkValue - The value to check for equality.
 * @param {string} [type] - Optional type hint to influence the comparison logic.
 * @returns {boolean} Returns `true` if the values are considered equal, otherwise `false`.
 */
const areValuesEqual = (referenceValue: unknown, checkValue: unknown, type?: string): boolean => {
  if (referenceValue === checkValue) {
    return true;
  }

  return areValuesEqualInternal(referenceValue, checkValue, 1, type);
};

export default areValuesEqual;
